import { PDFCheckSVG } from "@components/svgs/pdf-check";
import { PDFCrossSVG } from "@components/svgs/pdf-cross";
import { PDFStarSVG } from "@components/svgs/pdf-star";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import {
  BusinessMetricTypeId,
  FieldTypeId,
} from "@models/configuration/feature";
import {
  AnalysisDataDTO,
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { AnalysisDataValue } from "@models/platform-analysis/enums/dataValue";
import { FeatureAnalysisRowLevel as RowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { useTheme } from "@mui/material";
import { Image, Text, View } from "@react-pdf/renderer";
import { defaultTo, isEmpty, isNil } from "lodash";
import { HorizontalLine } from "../../export-pdf/components/horizontal-line";
import { PDFEssentialNotice } from "../../export-pdf/components/tables/essential-notice";
import { PDFTable } from "../../export-pdf/components/tables/table";
import { PDFTableBody } from "../../export-pdf/components/tables/table-body";
import { PDFTableCell } from "../../export-pdf/components/tables/table-cell";
import { PDFTableHeaderWithLogo } from "../../export-pdf/components/tables/table-header-with-logo";
import { PDFTableRow } from "../../export-pdf/components/tables/table-row";
import { BusinessMetricIcons, PDFStyleConfigs } from "../../export-pdf/configs";
import { generateFeatureSectionIconUrl } from "../../export-pdf/utils";

type PDFFeatureAnalysisProps = {
  selectedProducts?: SelectedProductDTO[];
  tableRows?: FeatureAnalysisRowDTO[];
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  title?: string;
  tableViewMode?: TableViewMode;
  isExpand?: boolean;
  isReview?: boolean;
  hideScore?: boolean;
  isBusinessMetric?: boolean;
  isAppendix?: boolean;
  startIndex?: number;
  endIndex?: number;
  numOfProducts?: number;
};

export const PDFFeatureAnalysis = ({
  selectedProducts,
  tableRows,
  productRecommendations,
  title,
  tableViewMode,
  isExpand = false,
  isReview = false,
  hideScore = false,
  isBusinessMetric = false,
  isAppendix,
  startIndex,
  endIndex,
  numOfProducts,
}: PDFFeatureAnalysisProps) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();

  const isDetailMode = tableViewMode === TableViewMode.Detail;
  const isDefaultMode = tableViewMode === TableViewMode.Default;
  const isSummaryMode = tableViewMode === TableViewMode.Summary;
  const shouldHideScore = hideScore && isDetailMode;
  const containsEssentialFeature = tableRows?.some((row) => row.isEssential);
  const containsMissingEssentialFeature = tableRows?.some((row) =>
    row.analysisData?.some((data) => data.isMissingEssential),
  );
  const showBusinessMetricNote =
    isBusinessMetric &&
    tableRows?.some((row) =>
      row.analysisData?.some(
        (data) => data?.fieldTypeId === FieldTypeId.TextLong,
      ),
    );

  let firstCellWidth = PDFStyleConfigs.TableFirstCellWidth;
  if (isDetailMode) firstCellWidth += PDFStyleConfigs.TableCellWidth;
  let tableWidth =
    firstCellWidth +
    defaultTo(selectedProducts?.length, 0) * PDFStyleConfigs.TableCellWidth;
  if (!isDetailMode) tableWidth += PDFStyleConfigs.TableCellWidth;

  const iconMapping = {
    [BusinessMetricTypeId.Esg]: BusinessMetricIcons?.Esg,
    [BusinessMetricTypeId.Operational]: BusinessMetricIcons?.Operational,
    [BusinessMetricTypeId.FinancialStrength]:
      BusinessMetricIcons?.FinancialStrength,
  };

  const renderImage = (
    isSubSectionType: boolean,
    row: FeatureAnalysisRowDTO,
  ) => {
    if (row.businessMetricTypeId) {
      const iconSrc = iconMapping[row.businessMetricTypeId];

      if (!iconSrc) return null;

      return <Image src={iconSrc} style={{ width: 20, height: 20 }} />;
    }

    if (isSubSectionType || isNil(row.iconUrl)) return null;

    const src = generateFeatureSectionIconUrl(row?.iconUrl);

    return <Image style={{ width: 10, height: 10 }} src={src} />;
  };

  const renderBooleanValue = (value: boolean) => {
    return value ? <PDFCheckSVG /> : <PDFCrossSVG />;
  };

  const renderPlatformAnalysisValue = (analysisData: AnalysisDataDTO) => {
    if (analysisData?.value === AnalysisDataValue.MissingData) {
      return (
        <Text style={{ ...typographyStyle.body2, color: palette.text.primary }}>
          No data
        </Text>
      );
    }

    const isYesNoField =
      analysisData?.fieldTypeId === FieldTypeId.YesNo ||
      [AnalysisDataValue.True, AnalysisDataValue.False].includes(
        analysisData?.value as AnalysisDataValue,
      );

    if (isYesNoField) {
      return renderBooleanValue(analysisData.value === "true");
    }

    if (analysisData?.fieldTypeId === FieldTypeId.TextShort) {
      return (
        <View
          style={{
            gap: PDFStyleConfigs.GapXSmall,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: 5,
            paddingTop: 5,
          }}
        >
          <Text
            style={{ ...typographyStyle.body2, color: palette.text.primary }}
          >
            {analysisData.value}
          </Text>
          {!isNil(analysisData?.isPositiveScore) &&
            renderBooleanValue(analysisData?.isPositiveScore)}
        </View>
      );
    }

    if (analysisData?.fieldTypeId === FieldTypeId.Number) {
      return (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text
            style={{ ...typographyStyle.body2, color: palette.text.primary }}
          >
            {analysisData.value}
          </Text>
          {!isNil(analysisData?.isPositiveScore) &&
            renderBooleanValue(analysisData?.isPositiveScore)}
        </View>
      );
    }

    if (analysisData?.fieldTypeId === FieldTypeId.TextLong) {
      const scoreValue = analysisData?.isPositiveScore;

      return (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            marginLeft: isNil(scoreValue) ? 0 : 4,
          }}
        >
          <Text
            style={{ ...typographyStyle.body2, color: palette.text.primary }}
          >
            {!isEmpty(analysisData?.value) ? "Available" : "No data"}
          </Text>
          {!isNil(scoreValue) && renderBooleanValue(scoreValue)}
          <Text
            style={{
              paddingLeft: isNil(scoreValue) ? 0 : scoreValue ? -4 : -2,
            }}
          >
            {!isEmpty(analysisData?.value) && (
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.error.main,
                  paddingTop: 2,
                }}
              >
                *
              </Text>
            )}
          </Text>
        </View>
      );
    }
  };

  const renderScores = ({
    analysisData,
    bold,
    isOverallScoreRow,
  }: {
    analysisData?: AnalysisDataDTO;
    bold?: boolean;
    isOverallScoreRow?: boolean;
  }) => {
    if (shouldHideScore) return null;

    const renderAsterisk = analysisData?.isMissingEssential && !isDetailMode;

    return (
      <Text
        style={{
          ...typographyStyle.body2,
          color:
            isOverallScoreRow && isDetailMode && !isBusinessMetric
              ? palette.common.white
              : palette.text.primary,
          fontWeight: bold ? "bold" : "normal",
        }}
      >
        {analysisData?.totalScore} %
        {renderAsterisk && (
          <Text
            style={{
              ...typographyStyle?.body2,
              color: palette.error.main,
            }}
          >
            *
          </Text>
        )}
      </Text>
    );
  };

  return (
    <View style={{ gap: 5 }}>
      <View
        style={{
          height: isAppendix
            ? PDFStyleConfigs.SubTitleHeight
            : PDFStyleConfigs.TitleHeight,
          gap: isAppendix ? 5 : 0,
        }}
      >
        <Text
          style={{
            ...typographyStyle.subtitle,
            color: palette.common.black,
            marginBottom: "2px",
          }}
        >
          {title}
          {isAppendix ? (
            isBusinessMetric ? (
              <Text style={{ fontWeight: "normal" }}>
                {
                  " of platforms that made it to business review stage but were not selected"
                }
              </Text>
            ) : (
              <Text style={{ fontWeight: "normal" }}>
                {
                  " of platforms that that made it to feature review but were not selected"
                }
              </Text>
            )
          ) : (
            ""
          )}
          {isExpand ? (
            <Text style={{ fontWeight: isAppendix ? "normal" : "bold" }}>
              {" (continued)"}
            </Text>
          ) : (
            ""
          )}
        </Text>
        {isAppendix && (
          <Text
            style={{
              ...typographyStyle.body1,
              color: palette.common.black,
            }}
          >
            {`(Platform ${
              startIndex ?? 0 + 1
            }-${endIndex} of ${numOfProducts})`}
          </Text>
        )}

        <HorizontalLine color={palette.common.black} />
      </View>

      <View style={{ gap: "5px" }}>
        <PDFTable tableWidth={tableWidth}>
          <PDFTableHeaderWithLogo
            isFeature={!isBusinessMetric}
            isDetailMode={isDetailMode}
            selectedProducts={selectedProducts}
            productRecommendations={productRecommendations}
            isReview={isReview}
          />

          <PDFTableBody>
            {tableRows?.map((row, rowIndex) => {
              const {
                level,
                isEssential,
                totalFeaturesSelected,
                totalFeatures,
                analysisData,
                isOverallScoreRow,
              } = row;
              const isSectionType = level === RowLevel.Section;
              const isSubSectionType = level === RowLevel.SubSection;
              const isFeatureType = level === RowLevel.Feature;

              const summaryRowColor = isOverallScoreRow ? "dark" : "white";
              const defaultRowColor = isOverallScoreRow
                ? "dark"
                : isSectionType
                ? "gray"
                : "white";
              const detailRowColor = isOverallScoreRow
                ? "primary"
                : isSectionType
                ? "dark"
                : isSubSectionType
                ? "gray"
                : "white";
              const colorRow = isDetailMode
                ? detailRowColor
                : isDefaultMode
                ? defaultRowColor
                : isSummaryMode
                ? summaryRowColor
                : defaultRowColor;

              const icon = renderImage(isSubSectionType, row);

              return (
                <PDFTableRow
                  key={rowIndex}
                  color={isBusinessMetric ? defaultRowColor : colorRow}
                >
                  <PDFTableCell alignItems="flex-start" width={firstCellWidth}>
                    <View
                      style={{
                        gap:
                          isReview && isFeatureType
                            ? PDFStyleConfigs.Gap2XSmall
                            : PDFStyleConfigs.GapXSmall,
                        flexDirection: "row",
                        alignItems: "center",
                        position: "relative",
                        width: "100%",
                      }}
                    >
                      {icon}
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          color:
                            isOverallScoreRow &&
                            isDetailMode &&
                            !isBusinessMetric
                              ? palette.common.white
                              : palette.text.primary,
                          fontWeight:
                            isSubSectionType || isFeatureType
                              ? "normal"
                              : "bold",
                          paddingRight: isEssential && isFeatureType ? 7 : 0,
                        }}
                      >
                        {row.name}
                        {isDetailMode &&
                          !isFeatureType &&
                          ` (${totalFeaturesSelected}/${totalFeatures})`}
                      </Text>
                      <View style={{ position: "absolute", right: 0 }}>
                        {isEssential && isFeatureType && <PDFStarSVG />}
                      </View>
                    </View>
                  </PDFTableCell>

                  {!isDetailMode && !isBusinessMetric && (
                    <PDFTableCell width={PDFStyleConfigs.TableCellWidth}>
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          color:
                            isOverallScoreRow && isDetailMode
                              ? palette.common.white
                              : palette.text.primary,
                          fontWeight: isSubSectionType ? "normal" : "bold",
                        }}
                      >
                        {`${totalFeaturesSelected}/${totalFeatures}`}
                      </Text>
                    </PDFTableCell>
                  )}

                  {analysisData?.map((data, dataIndex) => {
                    return (
                      <PDFTableCell
                        key={dataIndex}
                        width={PDFStyleConfigs.TableCellWidth}
                        paddingHorizontal={0}
                        paddingVertical={0}
                      >
                        {isFeatureType
                          ? renderPlatformAnalysisValue(data)
                          : renderScores({
                              analysisData: data,
                              bold: !isSubSectionType && !isFeatureType,
                              isOverallScoreRow,
                            })}
                      </PDFTableCell>
                    );
                  })}
                </PDFTableRow>
              );
            })}
          </PDFTableBody>
        </PDFTable>

        {containsMissingEssentialFeature && !isDetailMode && (
          <Text
            style={{
              ...typographyStyle?.body2,
              color: palette?.error?.main,
            }}
          >
            * Missing an essential feature
          </Text>
        )}

        {containsEssentialFeature && isDetailMode && <PDFEssentialNotice />}

        {showBusinessMetricNote && (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.error.main,
            }}
          >
            * See web version of this report for full details
          </Text>
        )}
      </View>
    </View>
  );
};
